import { useEffect, useRef, useState } from "react";
import { animate, useReducedMotion } from "framer-motion";
import { fmtUSD } from "@/lib/api";
import { Wallet, TrendingDown, CalendarClock, PiggyBank, FileWarning } from "lucide-react";

// Número que se anima de 0 (o su valor previo) hasta el nuevo valor en 0,8 s.
// Respeta prefers-reduced-motion: sin animación, muestra el valor final.
function AnimatedUSD({ value }) {
  const reduced = useReducedMotion();
  const fromRef = useRef(0);
  const [display, setDisplay] = useState(reduced ? (value ?? 0) : 0);

  useEffect(() => {
    const to = Number(value) || 0;
    if (reduced) {
      setDisplay(to);
      fromRef.current = to;
      return;
    }
    const controls = animate(fromRef.current, to, {
      duration: 0.8,
      ease: "easeOut",
      onUpdate: (v) => setDisplay(v),
    });
    fromRef.current = to;
    return () => controls.stop();
  }, [value, reduced]);

  return <>{fmtUSD(display)}</>;
}

function KpiSkeleton() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="kpis-skeleton">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-slate-100 animate-pulse shrink-0" />
          <div className="flex-1"><div className="h-6 w-2/3 rounded bg-slate-200 animate-pulse" /><div className="h-3 w-1/2 rounded bg-slate-100 animate-pulse mt-2" /></div>
        </div>
      ))}
    </div>
  );
}

// Fila de indicadores sin tarjeta: círculo crema con ícono + número grande + etiqueta (+ pill de contexto si existe).
export const CarteraKpis = ({ kpis, clientes = 0 }) => {
  if (!kpis) return <KpiSkeleton />;
  const pct = kpis.total_cartera > 0 ? Math.round((kpis.valor_vencido / kpis.total_cartera) * 100) : 0;

  const cards = [
    { testid: "kpi-total-cartera-card", label: "Total cartera (USD)", num: kpis.total_cartera, money: true, pill: `${clientes} clientes`, tone: "neutro", Icon: Wallet },
    { testid: "kpi-valor-vencido-card", label: "Valor vencido (USD)", num: kpis.valor_vencido, money: true, pill: `${pct}% de la cartera`, tone: pct > 0 ? "rojo" : "verde", Icon: TrendingDown },
    { testid: "kpi-valor-a-vencer-card", label: "A vencer (USD)", num: kpis.valor_a_vencer, money: true, Icon: CalendarClock },
    { testid: "kpi-cobrado-mes-card", label: "Cobrado del mes (USD)", num: kpis.cobrado_mes, money: true, Icon: PiggyBank },
    { testid: "kpi-pagares-pendientes-card", label: "Pagarés pendientes", value: String(kpis.pagares_pendientes ?? 0), pill: "de firma", tone: (kpis.pagares_pendientes ?? 0) > 0 ? "amarillo" : "verde", Icon: FileWarning },
  ];
  const PILL = { verde: "bg-[#007336] text-white", rojo: "bg-[#B8402A] text-white", amarillo: "bg-[#5FA8D9] text-[#24331F]", neutro: "tm-crema" };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-x-6 gap-y-5" data-testid="kpis-fila">
      {cards.map((c, i) => (
        <div key={c.testid} data-testid={c.testid} className="flex items-start gap-3 min-w-0 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
          <div className="h-12 w-12 rounded-full tm-crema flex items-center justify-center shrink-0"><c.Icon className="h-5 w-5" /></div>
          <div className="min-w-0">
            <div className="font-bold tabular-nums leading-tight text-slate-900 whitespace-nowrap text-[26px]">
              {c.money ? <AnimatedUSD value={c.num} /> : c.value}
            </div>
            <div className="text-xs font-medium text-slate-500 mt-1 leading-snug">{c.label}</div>
            {c.pill && <span className={`inline-block mt-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-bold whitespace-nowrap ${PILL[c.tone || "neutro"]}`}>{c.pill}</span>}
          </div>
        </div>
      ))}
    </div>
  );
};
